/**
 * Base Brand Colors and Material UI definitions.
 * Ingested from web/assets/Colours.png and standard Material Design 3 specifications.
 */

import { type IMaterialElevation } from "./types";

/**
 * Official Brand Palette ingested from web/assets/Colours.png
 */
export const BrandColors = {
  sungoldOrange: {
    name: "Sungold Orange",
    pantone: "Orange 021 U",
    hex: "#FF5B03",
    cmyk: "1-11-6-0",
  },
  lemonYellow: {
    name: "Lemon Yellow",
    pantone: "2003 U",
    hex: "#FFF24D",
    cmyk: "1-11-6-0",
  },
  butterBean: {
    name: "Butter Bean",
    pantone: "7604 U",
    hex: "#FDFAD8",
    cmyk: "1-11-6-0",
  },
  beetRed: {
    name: "Beet Red",
    pantone: "7621 U",
    hex: "#AB1717",
    cmyk: "1-11-6-0",
  },
  grassyGreen: {
    name: "Grassy Green",
    pantone: "371 U",
    hex: "#47510B",
    cmyk: "1-11-6-0",
  },
  petalPink: {
    name: "Petal Pink",
    pantone: "196 U",
    hex: "#FFB6A9",
    cmyk: "1-11-6-0",
  },
  seedGreen: {
    name: "Seed Green",
    pantone: "7604 U",
    hex: "#CAD23C",
    cmyk: "1-11-6-0",
  },
  blueLinen: {
    name: "Blue Linen",
    pantone: "7681 U",
    hex: "#A1AED1",
    cmyk: "1-11-6-0",
  },
  tangerine: {
    name: "Tangerine",
    pantone: "151 U",
    hex: "#F28500",
    cmyk: "0-55-100-0",
  },
  heliotrope: {
    name: "Heliotrope",
    pantone: "522 U",
    hex: "#C885B4",
    cmyk: "15-50-5-0",
  },
} as const;

/**
 * Blue Linen Range derived from official brand palette (web/assets/Colours.png)
 * Core Pantone 7681 U #A1AED1
 */
export const BlueLinenRange = {
  deepInk: "#161F36", // High-contrast archival navy ink for button text
  deepDenim: "#243250", // Dark slate linen denim
  archival: "#4A608F", // Archival Blue Linen - Primary button in light mode (5.2:1 contrast against white)
  classic: "#667CA8", // Classic midtone Blue Linen
  base: BrandColors.blueLinen.hex, // #A1AED1 Pantone 7681 U core brand swatch
  soft: "#8FA2CF", // Soft luminous Blue Linen - Primary button in dark mode
  border: "#BAC4DF", // Crisp Blue Linen border for secondary elements
  wash: "#DCE3F2", // Soft Blue Linen wash for secondary button background in light mode
  mutedDisabled: "#8F9EBF", // Muted Blue Linen for disabled buttons
} as const;

/**
 * Standard Material UI / Material Design 3 elevation levels for Android and cross-platform components.
 */
export const MaterialElevations: Record<"level0" | "level1" | "level2" | "level3" | "level4" | "level5", IMaterialElevation> = {
  level0: {
    elevation: 0,
    shadowColor: "transparent",
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  level1: {
    elevation: 1,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 2,
  },
  level2: {
    elevation: 2,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },
  level3: {
    elevation: 3,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.18,
    shadowRadius: 6,
  },
  level4: {
    elevation: 4,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  level5: {
    elevation: 5,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.24,
    shadowRadius: 10,
  },
};

/**
 * Standard Material Design 3 corner radius tokens.
 */
export const MaterialShapes = {
  none: 0,
  extraSmall: 4,
  small: 8,
  medium: 12,
  large: 16,
  extraLarge: 28,
  full: 9999,
} as const;

/**
 * Standard Design Typography tokens using the Inter font family.
 */
export const DesignTypography = {
  fontFamily: "Inter",
  sans: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  serif: "Georgia, Cambria, 'Times New Roman', Times, serif",
  mono: "SpaceMono, 'Courier New', monospace",
} as const;

/**
 * Official Memories Platform Design Schema specification.
 * Embeds standard brand colors, Inter typography, and UI shape geometries.
 */
export const DesignSchema = {
  fontFamily: "Inter",
  typography: DesignTypography,
  colors: BrandColors,
  shapes: MaterialShapes,
  elevations: MaterialElevations,
} as const;

